import { DayOfWeek, DAYS_OF_WEEK, OpeningTimes } from '@/types/common';
import { StoreBasicData, ChannelData, StoreData } from '@/types/store';
import {
  BackendRestaurantResponse,
  BackendBasicRestaurantResponse,
  BackendChannelGroup,
  BackendChannelInfoResponse,
  BackendChannelInfo,
  BackendOpeningTimes,
  ChannelGroup,
  ChannelType,
  StoreChannel,
  ChannelOpeningTimes,
} from '@/types/channels';

const BACKEND_DAY_TO_FRONTEND: Record<string, DayOfWeek> = {
  MONDAY: DAYS_OF_WEEK.MONDAY,
  TUESDAY: DAYS_OF_WEEK.TUESDAY,
  WEDNESDAY: DAYS_OF_WEEK.WEDNESDAY,
  THURSDAY: DAYS_OF_WEEK.THURSDAY,
  FRIDAY: DAYS_OF_WEEK.FRIDAY,
  SATURDAY: DAYS_OF_WEEK.SATURDAY,
  SUNDAY: DAYS_OF_WEEK.SUNDAY,
};

export const normalizeTime = (time: string): string => {
  if (!time) return '';
  return time.substring(0, 5);
};

export const normalizeDayKey = (day: string): DayOfWeek | null => {
  const normalized = BACKEND_DAY_TO_FRONTEND[day.toUpperCase()];
  return normalized || null;
};

export const normalizeOpeningTimes = (
  backendOpeningTimes: BackendOpeningTimes | null | undefined
): ChannelOpeningTimes | null => {
  if (!backendOpeningTimes) {
    return null;
  }

  if (backendOpeningTimes.failed) {
    return {
      maxNoOfRanges: backendOpeningTimes.maxNoOfRanges || 0,
      ranges: {},
      failed: true,
    };
  }

  const normalizedRanges: { [key in DayOfWeek]?: OpeningTimes[] } = {};

  if (backendOpeningTimes.ranges) {
    for (const [backendDay, ranges] of Object.entries(
      backendOpeningTimes.ranges
    )) {
      const frontendDay = normalizeDayKey(backendDay);
      if (frontendDay && Array.isArray(ranges)) {
        normalizedRanges[frontendDay] = ranges.map(range => ({
          startTime: normalizeTime(range.startTime),
          endTime: normalizeTime(range.endTime),
        }));
      }
    }
  }

  return {
    maxNoOfRanges: backendOpeningTimes.maxNoOfRanges || 0,
    ranges: normalizedRanges,
    ...(backendOpeningTimes.hoursUnavailable
      ? { hoursUnavailable: true }
      : {}),
    ...(backendOpeningTimes.forced24x7 ? { forced24x7: true } : {}),
  };
};

export const normalizeChannel = (
  channelKey: string,
  backendChannel: BackendChannelInfo | null | undefined
): StoreChannel | null => {
  if (!backendChannel) {
    return null;
  }

  return {
    name: backendChannel.channel?.readableName || channelKey,
    type: backendChannel.type,
    supportedFeatures: backendChannel.supportedFeatures || [],
    openingTimes: normalizeOpeningTimes(backendChannel.openingTimes),
    paused: null,
    pauseStatus: backendChannel.pauseStatus || null,
    pausedAt: backendChannel.pausedAt || null,
    unpauseAt: backendChannel.unpauseAt || null,
    openingTimesExpireAt: backendChannel.openingTimesExpireAt || null,
    inheritsGroupHours: backendChannel.inheritsGroupHours ?? null,
  };
};

export const normalizeChannelGroups = (
  backendGroups: Record<string, BackendChannelGroup> | null | undefined
): Record<string, ChannelGroup> => {
  const groups: Record<string, ChannelGroup> = {};
  for (const [groupKey, group] of Object.entries(backendGroups || {})) {
    groups[groupKey] = {
      openingTimes: normalizeOpeningTimes(group.openingTimes),
      supportedFeatures: group.supportedFeatures || [],
    };
  }
  return groups;
};

// Normalize basic store response (from /stores/{id})
export const normalizeBasicStoreResponse = (
  backend: BackendBasicRestaurantResponse
): StoreBasicData => {
  return {
    id: String(backend.id),
    name: backend.name,
    supportedFeatures: backend.supportedFeatures || [],
    brandName: backend.brandName || null,
    organisationId: backend.organisationId ?? null,
    marketId: backend.marketId ?? null,
  };
};

// Normalize channel info response (from /stores/{id}/info)
export const normalizeChannelInfoResponse = (
  backend: BackendChannelInfoResponse | null | undefined
): ChannelData => {
  if (!backend) {
    return {
      channels: {},
      channelGroups: {},
      timezone: null,
    };
  }
  const normalizedChannels: Record<string, StoreChannel> = {};

  if (backend.channels) {
    for (const [channelKey, channelInfo] of Object.entries(backend.channels)) {
      const normalizedChannel = normalizeChannel(channelKey, channelInfo);
      if (normalizedChannel) {
        normalizedChannels[channelKey] = normalizedChannel;
      }
    }
  }

  return {
    channels: normalizedChannels,
    channelGroups: normalizeChannelGroups(backend.channelGroups),
    timezone: backend.timezone || null,
  };
};

// Legacy function for backward compatibility
export const normalizeStoreResponse = (
  backend: BackendRestaurantResponse | null | undefined
): StoreData | null => {
  if (!backend) {
    return null;
  }

  const normalizedChannels: { [key: string]: StoreChannel } = {};

  if (backend.channels) {
    for (const [channelKey, channelInfo] of Object.entries(backend.channels)) {
      const normalizedChannel = normalizeChannel(channelKey, channelInfo);
      if (normalizedChannel) {
        normalizedChannels[channelKey] = normalizedChannel;
      }
    }
  }

  return {
    id: String(backend.id),
    name: backend.name || '',
    channels: normalizedChannels,
    supportedFeatures: backend.supportedFeatures || [],
  };
};

export const isOneMinuteRange = (
  startTime: string,
  endTime: string
): boolean => {
  if (
    !startTime ||
    !endTime ||
    !startTime.includes(':') ||
    !endTime.includes(':')
  )
    return false;
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);
  if (
    isNaN(startHours) ||
    isNaN(startMinutes) ||
    isNaN(endHours) ||
    isNaN(endMinutes)
  )
    return false;
  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;
  return endTotalMinutes - startTotalMinutes === 1;
};

export const determineOpeningHoursType = (
  ranges: OpeningTimes[] | undefined
): 'OPEN_ALL_DAY' | 'CLOSED_ALL_DAY' | 'OPEN_BETWEEN_TIMES' => {
  if (!ranges || ranges.length === 0) {
    return 'CLOSED_ALL_DAY';
  }

  if (ranges.length === 1) {
    const { startTime, endTime } = ranges[0];
    if (startTime === '00:00' && endTime === '23:59') {
      return 'OPEN_ALL_DAY';
    }
    if (isOneMinuteRange(startTime, endTime)) {
      return 'CLOSED_ALL_DAY';
    }
  }

  return 'OPEN_BETWEEN_TIMES';
};

export const getChannelOpeningTimesForDay = (
  channel: StoreChannel | undefined,
  day: DayOfWeek
): OpeningTimes[] => {
  if (!channel?.openingTimes?.ranges) {
    return [];
  }
  return channel.openingTimes.ranges[day] || [];
};

/** True when a group has hours of its own for the portal to show and inherit from. */
export const hasGroupOpeningTimes = (
  openingTimes: ChannelOpeningTimes | null | undefined
): boolean => Object.keys(openingTimes?.ranges || {}).length > 0;

export const getGroupOpeningTimesForDay = (
  openingTimes: ChannelOpeningTimes | null | undefined,
  day: DayOfWeek
): OpeningTimes[] => openingTimes?.ranges?.[day] || [];

/** True when the group holds an entry for the day, so no ranges reads as closed. */
export const isGroupOpeningTimesDaySet = (
  openingTimes: ChannelOpeningTimes | null | undefined,
  day: DayOfWeek
): boolean => !!openingTimes?.ranges && day in openingTimes.ranges;

export const hasOpeningTimesSupport = (
  channel: StoreChannel | undefined
): boolean => {
  if (!channel) return false;
  return channel.supportedFeatures.includes('OPENING_TIMES');
};

export const hasPauseChannelSupport = (
  channel: StoreChannel | undefined
): boolean => {
  if (!channel) return false;
  return channel.supportedFeatures.includes('PAUSE_CHANNEL');
};

export const hasTemporaryOpeningTimesSupport = (
  channel: StoreChannel | undefined
): boolean => {
  if (!channel) return false;
  return channel.supportedFeatures.includes('TEMPORARY_OPENING_TIMES');
};

export const getLiveTemporaryHoursExpiry = (
  channel: StoreChannel | undefined
): Date | null => {
  if (!channel?.openingTimesExpireAt) return null;
  const expiry = new Date(channel.openingTimesExpireAt);
  if (isNaN(expiry.getTime()) || expiry.getTime() <= Date.now()) return null;
  return expiry;
};

/**
 * The last day the live temporary hours apply, as YYYY-MM-DD in the store's timezone.
 * `openingTimesExpireAt` is the moment they stop, which is the midnight after that day.
 */
export const getLiveTemporaryHoursLastDay = (
  channel: StoreChannel | undefined,
  timezone?: string | null
): string | null => {
  const expiry = getLiveTemporaryHoursExpiry(channel);
  if (!expiry) return null;
  const lastDay = new Date(expiry.getTime() - 1000);
  const options: Intl.DateTimeFormatOptions = timezone
    ? { timeZone: timezone }
    : {};
  return new Intl.DateTimeFormat('en-CA', options).format(lastDay);
};

export const hasOpeningTimesFailed = (
  channel: StoreChannel | undefined
): boolean => {
  return channel?.openingTimes?.failed === true;
};

/**
 * True when the backend told us the provider holds no hours it could read. Such a channel can
 * still be edited: an update only writes the days it carries.
 */
export const hasUnavailableOpeningTimes = (
  channel: StoreChannel | undefined
): boolean => channel?.openingTimes?.hoursUnavailable === true;

/**
 * True when the channel is open 24/7 because the CMS "Override store hours to be open 24x7" option
 * is enabled for it. Its hours come from that option, so the portal can only show them.
 */
export const hasForced24x7Hours = (
  channel: StoreChannel | undefined
): boolean => channel?.openingTimes?.forced24x7 === true;

/** True when the channel returned hours of its own we could resend for an untouched day. */
export const hasReadableOpeningTimes = (
  channel: StoreChannel | undefined
): boolean => {
  const ranges = channel?.openingTimes?.ranges;
  if (!ranges || hasOpeningTimesFailed(channel)) return false;
  return Object.keys(ranges).length > 0;
};

export const getChannelsByType = (
  channels: Record<string, StoreChannel>,
  type: 'PROPRIETARY' | 'DELIVERY'
): string[] => {
  return Object.entries(channels)
    .filter(
      ([_, channelInfo]) =>
        channelInfo.type === type && hasPauseChannelSupport(channelInfo)
    )
    .map(([channelId]) => channelId)
    .sort((a, b) => {
      const aLabel = channels[a]?.name || a;
      const bLabel = channels[b]?.name || b;
      return aLabel.localeCompare(bLabel);
    });
};

export type OpeningHoursType =
  'OPEN_ALL_DAY' | 'CLOSED_ALL_DAY' | 'OPEN_BETWEEN_TIMES';

export interface DayOpeningHours {
  day: string;
  type: OpeningHoursType;
  openingHours?: OpeningTimes[];
}

export interface CompleteOpeningHoursPayload {
  channels?: string[];
  channelGroup?: ChannelType;
  overwriteOverriddenChannels?: boolean;
  days: DayOpeningHours[];
  open24x7: boolean;
  expiresAt?: string;
  clearOverride?: boolean;
}

const ALL_DAYS: DayOfWeek[] = [
  DAYS_OF_WEEK.MONDAY,
  DAYS_OF_WEEK.TUESDAY,
  DAYS_OF_WEEK.WEDNESDAY,
  DAYS_OF_WEEK.THURSDAY,
  DAYS_OF_WEEK.FRIDAY,
  DAYS_OF_WEEK.SATURDAY,
  DAYS_OF_WEEK.SUNDAY,
];

export const buildCompleteOpeningHoursPayload = (
  channel: string,
  selectedDays: DayOfWeek[],
  userType: OpeningHoursType,
  userOpeningHours: OpeningTimes[] | undefined,
  storeChannel: StoreChannel | undefined,
  expiresAt?: string,
  inheritedHours?: Partial<Record<DayOfWeek, OpeningTimes[]>>
): CompleteOpeningHoursPayload => {
  const days: DayOpeningHours[] = [];
  // The untouched days are only worth resending when the row rests on hours we actually read: an
  // update replaces the channel's whole week downstream, so guessing them closes days by accident.
  const canUseExistingHours =
    hasOpeningTimesSupport(storeChannel) &&
    !hasOpeningTimesFailed(storeChannel) &&
    !hasUnavailableOpeningTimes(storeChannel) &&
    (hasReadableOpeningTimes(storeChannel) ||
      Object.keys(inheritedHours || {}).length > 0);

  for (const day of ALL_DAYS) {
    const isUserSelectedDay = selectedDays.includes(day);

    if (isUserSelectedDay) {
      const dayEntry: DayOpeningHours = {
        day: day.toUpperCase(),
        type: userType,
      };
      if (userType === 'OPEN_BETWEEN_TIMES' && userOpeningHours) {
        dayEntry.openingHours = userOpeningHours;
      }
      days.push(dayEntry);
    } else if (canUseExistingHours) {
      // A channel with no hours of its own keeps the hours it currently shows, which are
      // the ones it inherits, rather than being closed for every day left unselected.
      const existingRanges =
        inheritedHours?.[day] ??
        getChannelOpeningTimesForDay(storeChannel, day);
      const existingType = determineOpeningHoursType(existingRanges);

      const dayEntry: DayOpeningHours = {
        day: day.toUpperCase(),
        type: existingType,
      };
      if (existingType === 'OPEN_BETWEEN_TIMES' && existingRanges.length > 0) {
        dayEntry.openingHours = existingRanges;
      }
      days.push(dayEntry);
    }
  }

  const open24x7 =
    days.length === 7 && days.every(d => d.type === 'OPEN_ALL_DAY');

  return {
    channels: [channel.toUpperCase()],
    days,
    open24x7,
    ...(expiresAt ? { expiresAt } : {}),
  };
};

const buildSelectedDays = (
  selectedDays: DayOfWeek[],
  userType: OpeningHoursType,
  userOpeningHours?: OpeningTimes[]
): DayOpeningHours[] =>
  ALL_DAYS.filter(day => selectedDays.includes(day)).map(day => {
    const dayEntry: DayOpeningHours = {
      day: day.toUpperCase(),
      type: userType,
    };
    if (userType === 'OPEN_BETWEEN_TIMES' && userOpeningHours) {
      dayEntry.openingHours = userOpeningHours;
    }
    return dayEntry;
  });

/**
 * Payload for updating several channels at once. Only the selected days are sent so the
 * days the user did not choose keep each channel's own existing hours.
 */
export const buildBulkOpeningHoursPayload = (
  channels: string[],
  selectedDays: DayOfWeek[],
  userType: OpeningHoursType,
  userOpeningHours?: OpeningTimes[]
): CompleteOpeningHoursPayload => {
  const days = buildSelectedDays(selectedDays, userType, userOpeningHours);

  return {
    channels: channels.map(channel => channel.toUpperCase()),
    days,
    open24x7: days.length === 7 && days.every(d => d.type === 'OPEN_ALL_DAY'),
  };
};

/**
 * Payload for updating a whole channel group. The backend resolves the channels and, unless
 * asked to overwrite them, leaves the ones with their own hours alone.
 */
export const buildGroupOpeningHoursPayload = (
  channelGroup: ChannelType,
  selectedDays: DayOfWeek[],
  userType: OpeningHoursType,
  userOpeningHours?: OpeningTimes[],
  overwriteOverriddenChannels = false,
  expiresAt?: string
): CompleteOpeningHoursPayload => {
  const days = buildSelectedDays(selectedDays, userType, userOpeningHours);

  return {
    channelGroup,
    overwriteOverriddenChannels,
    days,
    open24x7: days.length === 7 && days.every(d => d.type === 'OPEN_ALL_DAY'),
    ...(expiresAt ? { expiresAt } : {}),
  };
};

/** End temporary hours now: the channel goes back to the store's own hours. */
export const buildClearTemporaryHoursPayload = (
  channel: string
): CompleteOpeningHoursPayload => ({
  channels: [channel.toUpperCase()],
  days: [],
  open24x7: false,
  clearOverride: true,
});

export const formatTimeTo12Hour = (time: string): string => {
  if (!time || !time.includes(':')) return '';
  const [hours, minutes] = time.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return '';
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  if (minutes === 0) {
    return `${displayHours}${period}`;
  }
  return `${displayHours}:${minutes.toString().padStart(2, '0')}${period}`;
};

export const formatOpeningTimesDisplay = (
  ranges: OpeningTimes[] | undefined
): { type: 'single' | 'multiple' | 'none'; displayText?: string } => {
  if (!ranges || ranges.length === 0) {
    return { type: 'none' };
  }

  if (ranges.length === 1) {
    const { startTime, endTime } = ranges[0];
    if (startTime === '00:00' && endTime === '23:59') {
      return { type: 'none' };
    }
    const formattedStart = formatTimeTo12Hour(startTime);
    const formattedEnd = formatTimeTo12Hour(endTime);
    return { type: 'single', displayText: `${formattedStart}-${formattedEnd}` };
  }

  return { type: 'multiple' };
};
